import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer, useToast } from './components/ui/Toast';
import { validateToken } from './store/authSlice';

// Import pages
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import CarsPage from './pages/CarsPage';
import LocationsPage from './pages/LocationsPage';
// import RoutesPage from './pages/RoutesPage';
import NotificationsPage from './pages/NotificationsPage';
import CarFormPage from './pages/CarFormPage';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { TripsPage } from './pages/TripsPage';
import { FinancePage } from './pages/FinancePage';
import { UserForm } from './pages/UserFormPage';
import RouteProtectionTestPage from './pages/RouteProtectionTestPage';

import { DriverPage } from './pages/DriverPage';

// Import enhanced route protection components
import {
  ProtectedRoute,
  AdminRoute,
  UserManagementRoute,
  CarManagementRoute,
  TripManagementRoute,
  DriverManagementRoute,
  PublicRoute
} from './components/auth';

// Note: ProtectedRoute and PublicRoute components are now imported from ./components/auth

// Main App Component
function AppContent() {
  const dispatch = useDispatch();
  const { toasts, removeToast } = useToast();

  // Validate token on app startup
  useEffect(() => {
    dispatch(validateToken());
  }, [dispatch]);

  return (
    <>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Unauthorized Access Page */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected Routes - Dashboard (accessible to all authenticated users) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* User Management Routes - Admin and Super Admin only */}
      <Route
        path="/users"
        element={
          <UserManagementRoute>
            <UsersPage />
          </UserManagementRoute>
        }
      />
      <Route
        path="/users/add"
        element={
          <UserManagementRoute requiredPermission="users_create">
            <UserForm />
          </UserManagementRoute>
        }
      />
      <Route
        path="/users/edit/:id"
        element={
          <UserManagementRoute requiredPermission="users_edit">
            <UserForm />
          </UserManagementRoute>
        }
      />

      {/* Car Management Routes - Admin and Super Admin only */}
      <Route
        path="/cars"
        element={
          <CarManagementRoute>
            <CarsPage />
          </CarManagementRoute>
        }
      />
      <Route
        path="/cars/add"
        element={
          <CarManagementRoute requiredPermission="cars_create">
            <CarFormPage />
          </CarManagementRoute>
        }
      />
      <Route
        path="/cars/edit/:id"
        element={
          <CarManagementRoute requiredPermission="cars_edit">
            <CarFormPage />
          </CarManagementRoute>
        }
      />

      {/* Location Management Routes - Admin and Super Admin only */}
      <Route
        path="/locations"
        element={
          <AdminRoute>
            <LocationsPage />
          </AdminRoute>
        }
      />

      {/* Trip Management Routes - All authenticated users can view, admins can manage */}
      <Route
        path="/trips/*"
        element={
          <TripManagementRoute>
            <TripsPage />
          </TripManagementRoute>
        }
      />

      {/* Finance Routes - Admin and Super Admin only */}
      <Route
        path="/finance/*"
        element={
          <AdminRoute>
            <FinancePage />
          </AdminRoute>
        }
      />

      {/* Driver Management Routes - Admin and Super Admin only */}
      <Route
        path="/drivers/*"
        element={
          <DriverManagementRoute>
            <DriverPage />
          </DriverManagementRoute>
        }
      />

      {/* Notifications - All authenticated users */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Route Protection Test Page - All authenticated users */}
      <Route
        path="/test-protection"
        element={
          <ProtectedRoute>
            <RouteProtectionTestPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Catch-all route - redirect to dashboard for authenticated users */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />
    </Routes>

    <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </>
  );
}

export default function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
}